import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Code2, Eye, Pencil } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";

/**
 * HTML widget editor — single "About Us / Custom HTML" widget. Matches the
 * mindfulminerals.com widget pattern (paste HTML → preview → save).
 */
export default function HtmlWidget({ label, value, onChange, testId = "html-widget" }) {
  const [view, setView] = useState("edit");
  return (
    <div data-testid={testId} className="space-y-2">
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="grid grid-cols-2 bg-zinc-100 rounded-lg p-1 h-9 w-full">
          <TabsTrigger
            value="edit"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-medium gap-1"
            data-testid={`${testId}-tab-edit`}
          >
            <Pencil size={11} /> Edit HTML
          </TabsTrigger>
          <TabsTrigger
            value="preview"
            className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-medium gap-1"
            data-testid={`${testId}-tab-preview`}
          >
            <Eye size={11} /> Preview
          </TabsTrigger>
        </TabsList>
        <TabsContent value="edit" className="mt-2">
          <Textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={8}
            placeholder={`<h2>About my spa</h2>\n<p>I help clients glow with...</p>`}
            className="rounded-xl font-mono text-xs"
            data-testid={`${testId}-input`}
          />
          <div className="flex items-center justify-between mt-1 text-[11px] text-zinc-400">
            <span className="flex items-center gap-1">
              <Code2 size={11} /> Paste your HTML here
            </span>
            <span>{(value || "").length} characters</span>
          </div>
        </TabsContent>
        <TabsContent value="preview" className="mt-2">
          <Card className="rounded-xl border-zinc-200 p-4 bg-white min-h-[120px]">
            {value ? (
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
                data-testid={`${testId}-preview`}
              />
            ) : (
              <div className="text-sm text-zinc-400 italic">
                Nothing to preview yet — switch to Edit and paste some HTML.
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
